import React, { useState, useCallback } from 'react';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlay, FiPause, FiRotateCcw, FiSkipForward } from 'react-icons/fi';

const MotionBox = motion(Box);

const IconBtn = ({ icon: Icon, label, onClick, primary, accent, theme }) => {
  const [rippleKey, setRippleKey] = useState(0);

  const handleClick = useCallback((e) => {
    if (primary) setRippleKey((k) => k + 1);
    onClick(e);
  }, [onClick, primary]);

  return (
    <Tooltip label={label} placement="bottom" hasArrow openDelay={400}>
      <Box position="relative" display="inline-flex">
        {primary && (
          <MotionBox
            position="absolute"
            inset="-6px"
            borderRadius="full"
            pointerEvents="none"
            animate={{
              boxShadow: [
                `0 0 0 0px ${accent}00`,
                `0 0 0 8px ${accent}22`,
                `0 0 0 0px ${accent}00`,
              ],
            }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
        <MotionBox
          as="button"
          onClick={handleClick}
          aria-label={label}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          cursor="pointer"
          flexShrink={0}
          position="relative"
          zIndex={1}
          overflow="visible"
          whileHover={{ scale: primary ? 1.06 : 1.05 }}
          whileTap={{ scale: primary ? 0.94 : 0.92 }}
          transition={{ type: 'spring', stiffness: 420, damping: 22 }}
          style={
            primary
              ? {
                  width: '72px',
                  height: '72px',
                  background: `linear-gradient(145deg, ${accent} 0%, ${accent}cc 100%)`,
                  boxShadow: `0 0 36px ${accent}66, 0 8px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.25)`,
                  border: 'none',
                  color: 'rgba(0,0,0,0.88)',
                  transition: 'background 0.6s ease, box-shadow 0.4s ease',
                }
              : {
                  width: '46px',
                  height: '46px',
                  background: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`,
                  backdropFilter: 'blur(12px)',
                  color: theme.textMuted,
                  transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                }
          }
          _hover={
            !primary
              ? {
                  background: `${accent}12`,
                  borderColor: `${accent}40`,
                  color: theme.textPrimary,
                  boxShadow: `0 0 16px ${accent}18`,
                }
              : {
                  boxShadow: `0 0 48px ${accent}88, 0 10px 28px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3)`,
                }
          }
        >
          <AnimatePresence>
            {primary && (
              <motion.span
                key={rippleKey}
                initial={{ scale: 0.3, opacity: 0.45 }}
                animate={{ scale: 2.4, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.55, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 65%)`,
                  pointerEvents: 'none',
                }}
              />
            )}
          </AnimatePresence>
          <Icon size={primary ? 28 : 18} strokeWidth={primary ? 2.2 : 2} style={{ position: 'relative', zIndex: 1 }} />
        </MotionBox>
      </Box>
    </Tooltip>
  );
};

const TimerControls = ({ isRunning, onStart, onPause, onReset, onSkip, theme, accent }) => (
  <Flex align="center" gap={{ base: 5, md: 6 }} role="group" aria-label="Timer controls">
    <IconBtn
      icon={FiRotateCcw}
      label="Reset — R"
      onClick={onReset}
      accent={accent}
      theme={theme}
    />
    <IconBtn
      icon={isRunning ? FiPause : FiPlay}
      label={isRunning ? 'Pause — Space' : 'Start — Space'}
      onClick={isRunning ? onPause : onStart}
      primary
      accent={accent}
      theme={theme}
    />
    <IconBtn
      icon={FiSkipForward}
      label="Skip — S"
      onClick={onSkip}
      accent={accent}
      theme={theme}
    />
  </Flex>
);

export default TimerControls;
